import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  CHECKLIST_INPUT_TYPES,
  newTemplateInputField,
  FIELD_RESPONSE_CODES,
  FIELD_RESPONSE_MODES,
  fieldResponseMode,
} from "./checklistInputFields";

export default function ChecklistTypeCustomFieldsEditor({
  fields = [],
  editable = true,
  onChange,
}) {
  const { t } = useTranslation();
  // Keep draft rows (empty label) while editing — normalize only on API save
  const rows = Array.isArray(fields) ? fields : [];

  const updateRows = (next) => {
    onChange?.(next);
  };

  const updateRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    updateRows(next);
  };

  const addRow = () => {
    updateRows([...rows, newTemplateInputField()]);
  };

  const removeRow = (index) => {
    updateRows(rows.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div>
        <p className="text-xs font-semibold text-gray-800">
          {t("quality.checklist.type.customFieldsTitle")}
        </p>
        <p className="text-xs text-gray-500">
          {t("quality.checklist.type.customFieldsHint")}
        </p>
      </div>

      {rows.map((row, index) => (
        <div
          key={`field-${index}`}
          className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-white p-3 sm:grid-cols-12"
        >
          <div className="sm:col-span-4">
            <label className="text-[11px] font-medium text-gray-600">
              {t("quality.checklist.type.customFieldLabel")}
            </label>
            <input
              type="text"
              disabled={!editable}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={row.label || ""}
              onChange={(e) => updateRow(index, { label: e.target.value })}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="text-[11px] font-medium text-gray-600">
              {t("quality.checklist.type.customFieldType")}
            </label>
            <select
              disabled={!editable}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={row.input_type || "text"}
              onChange={(e) => updateRow(index, { input_type: e.target.value })}
            >
              {CHECKLIST_INPUT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`quality.checklist.type.customFieldTypes.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-medium text-gray-600">
              {t("quality.checklist.type.customFieldUnit")}
            </label>
            <input
              type="text"
              disabled={!editable}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={row.unit || ""}
              onChange={(e) => updateRow(index, { unit: e.target.value })}
            />
          </div>
          <div className="sm:col-span-3">
            <label className="text-[11px] font-medium text-gray-600">
              {t("quality.checklist.type.customFieldPlaceholder")}
            </label>
            <input
              type="text"
              disabled={!editable}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={row.placeholder || ""}
              onChange={(e) =>
                updateRow(index, { placeholder: e.target.value })
              }
            />
          </div>
          <div className="flex items-end justify-between gap-2 sm:col-span-1">
            <label className="flex items-center gap-1.5 pb-2 text-xs text-gray-700">
              <input
                type="checkbox"
                disabled={!editable}
                checked={Boolean(row.required)}
                onChange={(e) =>
                  updateRow(index, { required: e.target.checked })
                }
              />
              {t("quality.checklist.type.customFieldRequired")}
            </label>
            {editable && (
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <fieldset className="grid gap-2 sm:col-span-12 sm:grid-cols-4">
            <legend className="text-xs font-medium text-gray-600">{t("quality.checklist.type.fieldResponseRules")}</legend>
            {FIELD_RESPONSE_CODES.map((code) => (
              <label key={code} className="text-xs text-gray-700">
                {t(`quality.checklist.actions.${code}`)}
                <select
                  aria-label={`${row.label || index + 1}: ${t(`quality.checklist.actions.${code}`)}`}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5"
                  disabled={!editable}
                  value={fieldResponseMode(row, code)}
                  onChange={(event) => updateRow(index, { response_rules: { ...row.response_rules, [code]: event.target.value } })}
                >
                  {FIELD_RESPONSE_MODES.map((mode) => <option key={mode} value={mode}>{t(`quality.checklist.type.fieldMode.${mode}`)}</option>)}
                </select>
              </label>
            ))}
          </fieldset>
        </div>
      ))}

      {editable && (
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-teal-400 hover:text-teal-700"
        >
          <PlusIcon className="h-4 w-4" />
          {t("quality.checklist.type.customFieldAdd")}
        </button>
      )}
    </div>
  );
}
